// Data catalog for the "Baumaterial" (building materials) wizard.
// Each material has a `unitType` that determines which quantity form is
// shown (see MaterialQuantityInput in SpecialItemsWizard.jsx):
//   piece    -> simple counter ("Anzahl")
//   package  -> counter + weight-per-unit bucket ("Anzahl der Säcke ...")
//   area     -> m² number input + optional package/pallet counter
//   length   -> total length number input + longest-piece length bucket
//   bulk     -> packaging type + weight bucket (sand/gravel...)
//   pallet   -> pieces + packaging (tiles/bricks/pallets/stone slabs)
//   custom   -> "Sonstiges" free text + qty + weight bucket

export const BUILDING_MATERIALS = [
  { key: "Fliesen", unitType: "pallet", subtype: "tiles" },
  { key: "Natursteinplatten", unitType: "pallet", subtype: "slabs" },
  { key: "Marmor / Granit", unitType: "pallet", subtype: "slabs" },
  { key: "Ziegelsteine", unitType: "pallet", subtype: "bricks" },
  { key: "Pflastersteine", unitType: "pallet", subtype: "bricks" },
  { key: "Betonsteine", unitType: "pallet", subtype: "bricks" },
  { key: "Zement", unitType: "package" },
  { key: "Mörtel / Putz", unitType: "package" },
  { key: "Sand", unitType: "bulk" },
  { key: "Kies / Schotter", unitType: "bulk" },
  { key: "Gipskartonplatten", unitType: "length", subtype: "sheet" },
  { key: "Dämmmaterial", unitType: "area" },
  { key: "Holzplatten", unitType: "length", subtype: "sheet" },
  { key: "Holzbalken", unitType: "length" },
  { key: "Bauholz", unitType: "length" },
  { key: "Metallprofile", unitType: "length" },
  { key: "Stahlträger", unitType: "length" },
  { key: "Rohre", unitType: "length" },
  { key: "Kabelrollen", unitType: "length" },
  { key: "Türen", unitType: "piece", glazable: true },
  { key: "Fenster", unitType: "piece", glazable: true },
  { key: "Sanitäranlagen", unitType: "piece", packagedCheck: true },
  { key: "Badewanne", unitType: "piece", packagedCheck: true },
  { key: "Duschkabine", unitType: "piece", packagedCheck: true },
  { key: "Heizkörper", unitType: "piece" },
  { key: "Bodenbelag", unitType: "area" },
  { key: "Laminat / Parkett", unitType: "area" },
  { key: "Tapetenrollen", unitType: "piece" },
  { key: "Farbeimer", unitType: "package" },
  { key: "Werkzeuge / Maschinen", unitType: "machine" },
  { key: "Gerüstteile", unitType: "piece" },
  { key: "Sonstiges Baumaterial", unitType: "custom", requiresDescription: true },
];

export const PACKAGE_WEIGHT_BUCKETS = ["Bis 10 kg", "11–25 kg", "26–40 kg", "Über 40 kg", "Nicht bekannt"];

export const BULK_PACKAGING_OPTIONS = ["In Säcken", "In Big Bags", "Lose", "Auf Paletten"];
export const BULK_WEIGHT_BUCKETS = ["Bis 250 kg", "251–500 kg", "501–1.000 kg", "1–2 Tonnen", "Über 2 Tonnen", "Nicht bekannt"];

export const TILE_PACKAGING_OPTIONS = ["In Kartons", "Auf Paletten", "Lose"];
export const TILE_WEIGHT_BUCKETS = ["Bis 250 kg", "251–500 kg", "501–1.000 kg", "Über 1.000 kg", "Nicht bekannt"];

export const SLAB_PACKAGING_OPTIONS = ["Einzeln", "Auf einer Palette", "Auf einem Transportgestell", "Nicht bekannt"];
export const SLAB_SIZE_BUCKETS = ["Bis 100 × 100 cm", "Bis 200 × 100 cm", "Größer als 200 × 100 cm", "Nicht bekannt"];
export const SLAB_WEIGHT_BUCKETS = ["Bis 50 kg pro Platte", "51–100 kg pro Platte", "Über 100 kg pro Platte", "Nicht bekannt"];

export const BRICK_PACKAGING_OPTIONS = ["Auf Paletten", "In Kisten", "Lose"];
export const BRICK_WEIGHT_BUCKETS = ["Bis 500 kg", "501–1.000 kg", "1–2 Tonnen", "Über 2 Tonnen", "Nicht bekannt"];

export const SHEET_LENGTH_BUCKETS = ["Bis 2 Meter", "2–3 Meter", "Über 3 Meter", "Nicht bekannt"];

export const LENGTH_BUCKETS = ["Bis 2 Meter", "2–3 Meter", "3–4 Meter", "4–6 Meter", "Über 6 Meter", "Nicht bekannt"];
export const LENGTH_WEIGHT_BUCKETS = ["Bis 25 kg", "26–50 kg", "51–100 kg", "Über 100 kg", "Nicht bekannt"];

export const GLAZED_OPTIONS = ["Ja", "Nein", "Teilweise"];
export const DOOR_WINDOW_SIZE_OPTIONS = ["Klein", "Mittel", "Groß", "Sondermaß"];
export const PACKAGED_OPTIONS = ["Ja", "Nein", "Teilweise"];

export const MACHINE_TYPES = [
  "Bohrmaschine", "Kreissäge", "Fliesenschneider", "Betonmischer",
  "Kompressor", "Rüttelplatte", "Baugerüst", "Generator", "Sonstiges",
];
export const MACHINE_WEIGHT_BUCKETS = ["Bis 25 kg", "26–50 kg", "51–100 kg", "101–250 kg", "Über 250 kg", "Nicht bekannt"];

export const CUSTOM_MATERIAL_WEIGHT_BUCKETS = ["Bis 100 kg", "101–250 kg", "251–500 kg", "501–1.000 kg", "Über 1.000 kg", "Nicht bekannt"];

export const TOTAL_WEIGHT_BUCKETS = ["Bis 250 kg", "251–500 kg", "501–1.000 kg", "1–2 Tonnen", "2–3,5 Tonnen", "Über 3,5 Tonnen", "Nicht bekannt"];

export const PACKAGING_METHODS = [
  "In Kartons", "In Säcken", "Auf Paletten", "In Big Bags", "In Kisten",
  "Gebündelt", "Lose", "Nicht verpackt", "Unterschiedlich verpackt", "Nicht bekannt",
];

export const PALLET_TYPES = ["Europalette", "Einwegpalette", "Sonderpalette", "Nicht bekannt"];
export const PALLET_WEIGHT_BUCKETS = ["Bis 250 kg", "251–500 kg", "501–750 kg", "751–1.000 kg", "Über 1.000 kg", "Nicht bekannt"];

export const LOCATION_TYPES = ["Erdgeschoss", "Obergeschoss", "Keller", "Garage", "Lagerhalle", "Baustelle", "Außenbereich", "Sonstiges"];

export const CARRYING_DISTANCE_BUCKETS = ["Bis 10 Meter", "11–25 Meter", "26–50 Meter", "Über 50 Meter", "Nicht bekannt"];
export const VEHICLE_ACCESS_DISTANCE_BUCKETS = ["Bis 25 Meter", "26–50 Meter", "Über 50 Meter", "Nicht bekannt"];

export const LOADING_SERVICE_OPTIONS = ["Nur Transport", "Nur Beladung", "Nur Entladung", "Beladung und Entladung"];
export const HELPERS_OPTIONS = ["1 Helfer", "2 Helfer", "3 Helfer", "4 oder mehr Helfer", "Ich bin nicht sicher"];

export const SPECIAL_EQUIPMENT_OPTIONS = [
  "Nein", "Hubwagen", "Sackkarre", "Möbelroller / Transportroller",
  "Hebebühne", "Gabelstapler", "Kran", "Tragegurte", "Ich bin nicht sicher",
];
export const EQUIPMENT_NEEDS_PROVIDER = ["Gabelstapler", "Kran"];
export const EQUIPMENT_PROVIDER_OPTIONS = ["Der Kunde", "Das Transportunternehmen", "Nicht bekannt"];

export const DEMONTAGE_ANSWER_OPTIONS = ["Ja", "Nein", "Ich bin nicht sicher"];
export const DEMONTAGE_SERVICE_OPTIONS = ["Nur Demontage", "Nur Montage", "Demontage und Montage"];

// Only items from BUILDING_MATERIALS that can realistically need
// Demontage/Montage; filtered further at runtime to only what was selected.
export const DEMONTAGE_ELIGIBLE_ITEMS = [
  "Türen", "Fenster", "Heizkörper", "Sanitäranlagen", "Duschkabine",
  "Küchen- oder Arbeitsplatten", "Regalsysteme", "Metallkonstruktionen",
  "Gerüstteile", "Sonstiges",
];

export const PHOTO_RECOMMENDED_FOR = [
  "Marmor / Granit", "Natursteinplatten", "Sand", "Kies / Schotter",
  "Holzbalken", "Stahlträger", "Metallprofile", "Werkzeuge / Maschinen",
];